import React, { useState, useCallback, useMemo, createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSegment } from './useSegment';
import { useLaunchWindowsConfig } from './useLaunchWindowsConfig';
import { resolveNonClubRelease } from '../utils/launchWindows';

function removeAcentos(newStringComAcento) {
  var semAcento = newStringComAcento.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return semAcento;
}

function hasHighLight(name, auction) {
  return auction?.summary?.highlights?.some(highlight => {
    return (
      removeAcentos(highlight.description.toLowerCase()) === removeAcentos(name.toLowerCase()) ||
      removeAcentos(highlight.description.toLowerCase()) ===
        removeAcentos('Perícia Cautelar aprovada'.toLowerCase())
    );
  });
}

const vehicleHasBuyFor = auction => auction.summary?.buyForPrice?.enabled;

export function hasValityPaper(auction) {
  const isD2D = auction?.summary?.hasD2D || false;

  // Para D2D, verificar também outras propriedades além dos highlights
  if (isD2D) {
    const hasPrecautionaryReport =
      auction?.summary?.precautionaryReport ||
      auction?.vehicle?.precautionaryReport ||
      (auction?.summary?.additionalInfo?.precautionaryStatus &&
        String(auction.summary.additionalInfo.precautionaryStatus)
          .toLowerCase()
          .includes('aprovad'));

    return (
      hasHighLight('Laudo Cautelar: Aprovado', auction) ||
      hasHighLight('Pericia Cautlelar Aprovada', auction) ||
      hasPrecautionaryReport
    );
  }

  // Para não-D2D, manter a lógica original
  return (
    hasHighLight('Laudo Cautelar: Aprovado', auction) ||
    hasHighLight('Pericia Cautlelar Aprovada', auction)
  );
}

const vehiclePercentageFipe = auction => {
  let fipe = 0;
  const currentOffer = auction?.auction?.winningBid?.amount || 0;
  const marketPrice = auction.summary?.marketPrice;

  if (!!marketPrice && marketPrice > 100) {
    fipe = Math.trunc((100 * currentOffer) / marketPrice) || 0;
  }

  return fipe <= 70;
};

const vehicleFipe100k = auction => auction.summary?.marketPrice >= 100000;

const vehicleFipe70k = auction => auction.summary?.marketPrice >= 70000;

const vehicleFipe80k = auction => auction.summary?.marketPrice >= 80000;

const vehicleScore80 = auction => {
  let score = 0;
  const goodPointsCount = Math.round(auction.summary?.goodPointsCount);

  if (!!goodPointsCount) {
    score = goodPointsCount;
  }

  return score >= 80;
};

const vehicleCloseToEnd = auction => {
  let closeToEnd = false;
  if (!!auction?.auction?.remainingTime) {
    closeToEnd = auction?.auction?.remainingTime <= 30 * 60 * 1000; // 30 minutes
  }

  return closeToEnd;
};

const vehicleMostViewed = auction => {
  let mostViewed = auction?.auction?.viewCount || 0;
  return mostViewed >= 8;
};

const vehicleBefore100k = auction => {
  let before100k = false;
  if (!!auction?.summary?.km) {
    before100k = auction?.summary?.km <= 100000;
  }

  return before100k;
};

// Sem passagem por leilão - verifica se não há histórico de leilão
const vehicleNoAuctionHistory = auction => {
  const historics = auction?.summary?.documentResearch?.historics || [];
  // Verifica se não há menção a leilão no histórico
  const hasAuctionHistory = historics.some(historic => {
    const lowerHistoric = String(historic).toLowerCase();
    return lowerHistoric.includes('leilão') || lowerHistoric.includes('leilao');
  });
  return !hasAuctionHistory;
};

// Longarina sem avarias - só exibir se Cautelar diferente de APROVADA
const vehicleCanHaveCarSparBadge = auction => {
  const hasCautelar = hasValityPaper(auction);
  const canHaveBadge =
    auction?.summary?.canHaveCarSparBadge || auction?.vehicle?.canHaveCarSparBadge;
  return canHaveBadge && !hasCautelar;
};

// Boa KM por ano - até 11.000 km rodados por ano
const vehicleGoodKmPerYear = auction => {
  const km = auction?.summary?.km;
  const productionYear = auction?.summary?.productionYear || auction?.summary?.year;

  if (!km || !productionYear) return false;

  const currentYear = new Date().getFullYear();
  const vehicleAge = currentYear - productionYear;

  if (vehicleAge <= 0) return false;

  const kmPerYear = km / vehicleAge;
  return kmPerYear <= 11000;
};

// Motor sem apontamentos - só exibir se diferente de MECÂNICA SEM AVARIAS
const vehicleCanHaveEngineBadge = auction => {
  // Verifica se o veículo tem a badge de motor sem apontamentos
  const canHaveBadge = auction?.summary?.canHaveEngineBadge || auction?.vehicle?.canHaveEngineBadge;

  // Verifica se não é MECÂNICA SEM AVARIAS (verificar nos highlights ou condições)
  const inspectionCondition = auction?.summary?.inspectionCondition;
  const isMechanicWithoutIssues =
    inspectionCondition === 'MECÂNICA SEM AVARIAS' ||
    String(inspectionCondition || '')
      .toUpperCase()
      .includes('MECÂNICA SEM AVARIAS');

  return canHaveBadge && !isMechanicWithoutIssues;
};

// Cautelar com apontamento - laudo reprovado ou com apontamentos
const vehicleCautelarWithIssues = auction => {
  const isD2D = auction?.summary?.hasD2D || false;

  if (isD2D) {
    // Para D2D, verificar o status da cautelar
    const precautionaryStatus = auction?.summary?.additionalInfo?.precautionaryStatus;
    if (precautionaryStatus) {
      const statusLower = String(precautionaryStatus).toLowerCase();
      // Se não contém "aprovad", então tem apontamento
      return !statusLower.includes('aprovad');
    }
    // Se não tem status, verificar se tem precautionaryReport mas não está aprovado
    const hasPrecautionaryReport =
      auction?.summary?.precautionaryReport || auction?.vehicle?.precautionaryReport;
    return hasPrecautionaryReport && !hasValityPaper(auction);
  }

  // Para não-D2D, verificar se tem highlights de cautelar mas não está aprovado
  const hasCautelarHighlight =
    hasHighLight('Laudo Cautelar', auction) || hasHighLight('Pericia Cautlelar', auction);

  if (hasCautelarHighlight) {
    // Se tem highlight mas não está aprovado, então tem apontamento
    return !hasValityPaper(auction);
  }

  return false;
};

const hasClubeBeneficios = (auction, profile, days) => {
  if (!auction?.auction?.startTime || !auction?.auction?.expectedEndTime) {
    return false;
  }

  const startTime = new Date(auction.auction.startTime).getTime();
  const endTime = new Date(auction.auction.expectedEndTime).getTime();

  if (isNaN(startTime) || isNaN(endTime)) {
    return false;
  }

  const now = Date.now();

  // Liberação para não-clube baseada na subida de lotes (generalReleaseTime do lote
  // do dia da subida). !isLiberatedTime = ainda exclusivo ao clube.
  const release = resolveNonClubRelease({ days, startTime, now });
  let isLiberatedTime;
  if (release.status === 'gated') {
    isLiberatedTime = now >= release.epoch;
  } else if (release.status === 'always') {
    isLiberatedTime = true;
  } else {
    isLiberatedTime = now >= startTime + 60 * 60 * 1000;
  }

  return (
    (!isLiberatedTime && profile?.benefitsClub === true) ||
    (profile?.email?.endsWith('@instacarro.com') && !isLiberatedTime)
  );
};

export const buyFor = list => list.filter(vehicleHasBuyFor);
export const cautelar = list => list.filter(hasValityPaper);
export const fipe70 = list => list.filter(vehiclePercentageFipe);
export const fipe100k = list => list.filter(vehicleFipe100k);
export const fipe70k = list => list.filter(vehicleFipe70k);
export const fipe80k = list => list.filter(vehicleFipe80k);
export const score80 = list => list.filter(vehicleScore80);
export const before100k = list => list.filter(vehicleBefore100k);
export const mostViewed = list => list.filter(vehicleMostViewed);
export const closeToEnd = list => list.filter(vehicleCloseToEnd);
export const clubeBeneficios = (list, profile, days = []) =>
  list.filter(auction => hasClubeBeneficios(auction, profile, days));
export const noAuctionHistory = list => list.filter(vehicleNoAuctionHistory);

// New filter functions for the new categories
const vehicleCanHaveColumnBadge = auction => {
  const hasCautelar = hasValityPaper(auction);
  const canHaveBadge = auction?.summary?.canHaveColumnBadge || auction?.vehicle?.canHaveColumnBadge;
  return canHaveBadge && !hasCautelar;
};
const vehicleCanHaveTransmissionBadge = auction => {
  // Só exibir se diferente de MECÂNICA SEM AVARIAS
  const inspectionCondition = auction?.summary?.inspectionCondition;
  const isMechanicWithoutIssues =
    inspectionCondition === 'MECÂNICA SEM AVARIAS' ||
    String(inspectionCondition || '')
      .toUpperCase()
      .includes('MECÂNICA SEM AVARIAS');
  const canHaveBadge =
    auction?.summary?.canHaveTransmissionBadge || auction?.vehicle?.canHaveTransmissionBadge;
  return canHaveBadge && !isMechanicWithoutIssues;
};
const vehicleCanHaveScannerBadge = auction => {
  // Scanner sem apontamentos - item de SCANNER sinalizado como CONFORME
  return auction?.summary?.canHaveScannerBadge || auction?.vehicle?.canHaveScannerBadge;
};
const vehicleCanHaveGNVBadge = auction =>
  auction?.summary?.canHaveGNVBadge || auction?.vehicle?.canHaveGNVBadge;
const vehicleCanHaveBlindagemBadge = auction =>
  auction?.summary?.canHaveBlindagemBadge || auction?.vehicle?.canHaveBlindagemBadge;
const vehicleEligibleForWarranty = auction => {
  // Elegível para Garantia: até 180 mil km, até 12 anos e sem avarias no motor, câmbio parado e fumaça no escapamento
  const km = auction?.summary?.km;
  const productionYear = auction?.summary?.productionYear || auction?.summary?.year;

  if (!km || !productionYear) return false;

  // Verificar km (≤ 180.000km)
  if (km > 180000) return false;

  // Verificar idade (≤ 12 anos)
  const currentYear = new Date().getFullYear();
  const vehicleAge = currentYear - productionYear;
  if (vehicleAge > 12) return false;

  // Verificar se tem a propriedade eligibleForWarranty (que já deve considerar motor, câmbio e fumaça)
  return auction?.summary?.eligibleForWarranty || auction?.vehicle?.eligibleForWarranty;
};

export const canHaveCarSparBadge = list => list.filter(vehicleCanHaveCarSparBadge);
export const canHaveColumnBadge = list => list.filter(vehicleCanHaveColumnBadge);
export const canHaveTransmissionBadge = list => list.filter(vehicleCanHaveTransmissionBadge);
export const canHaveScannerBadge = list => list.filter(vehicleCanHaveScannerBadge);
export const canHaveGNVBadge = list => list.filter(vehicleCanHaveGNVBadge);
export const canHaveBlindagemBadge = list => list.filter(vehicleCanHaveBlindagemBadge);
export const eligibleForWarranty = list => list.filter(vehicleEligibleForWarranty);
export const goodKmPerYear = list => list.filter(vehicleGoodKmPerYear);
export const canHaveEngineBadge = list => list.filter(vehicleCanHaveEngineBadge);
export const cautelarWithIssues = list => list.filter(vehicleCautelarWithIssues);

// Registro central de predicados: cada categoria mapeia para uma função
// (auction, profile) => boolean. Usado tanto para filtrar a listagem quanto
// para calcular os contadores dinâmicos (funil AND) dos chips.
const filterPredicates = {
  buyFor: vehicleHasBuyFor,
  cautelar: hasValityPaper,
  fipe70: vehiclePercentageFipe,
  fipe100k: vehicleFipe100k,
  fipe70k: vehicleFipe70k,
  fipe80k: vehicleFipe80k,
  score80: vehicleScore80,
  before100k: vehicleBefore100k,
  mostViewed: vehicleMostViewed,
  closeToEnd: vehicleCloseToEnd,
  clubeBeneficios: (auction, profile, days) => hasClubeBeneficios(auction, profile, days),
  noAuctionHistory: vehicleNoAuctionHistory,
  // New categories
  canHaveCarSparBadge: vehicleCanHaveCarSparBadge,
  canHaveColumnBadge: vehicleCanHaveColumnBadge,
  canHaveTransmissionBadge: vehicleCanHaveTransmissionBadge,
  canHaveScannerBadge: vehicleCanHaveScannerBadge,
  canHaveGNVBadge: vehicleCanHaveGNVBadge,
  canHaveBlindagemBadge: vehicleCanHaveBlindagemBadge,
  eligibleForWarranty: vehicleEligibleForWarranty,
  goodKmPerYear: vehicleGoodKmPerYear,
  canHaveEngineBadge: vehicleCanHaveEngineBadge,
  cautelarWithIssues: vehicleCautelarWithIssues,
};

// Aplica um conjunto de filtros (AND) sobre uma lista.
const applyFilters = (list, activeFilters, profile, days) =>
  activeFilters.reduce((acc, name) => {
    const predicate = filterPredicates[name];
    return predicate ? acc.filter(auction => predicate(auction, profile, days)) : acc;
  }, list);

// Contadores dinâmicos: para cada categoria, quantos veículos atenderiam se
// aquele chip fosse ativado JUNTO com os filtros já selecionados (funil AND).
// Para um chip já ativo, o valor é a própria contagem da lista filtrada atual
// (adicioná-lo de novo é idempotente).
const getLengths = (baseList, activeFilters, profile, days) => {
  return Object.keys(filterPredicates).reduce((lengths, name) => {
    const combined = activeFilters.includes(name) ? activeFilters : [...activeFilters, name];
    lengths[name] = applyFilters(baseList, combined, profile, days).length;
    return lengths;
  }, {});
};

const FilterContext = createContext({});

export function FilterProvider({ children }) {
  const auctions = useSelector(state => state.auctions?.newOffers || []);
  const showFilter = useSelector(state => state.ui.filters.showNewOffersFilters);
  const { profile } = useSelector(state => state.user || {});
  const { days: launchWindowsDays } = useLaunchWindowsConfig();

  const [filterList, setFilterList] = useState([]);
  const [staticAuctions, setStaticAuctions] = useState([]);

  const { trackFilter } = useSegment();

  const toogleFilter = useCallback(
    filter => {
      if (filterList.includes(filter)) {
        setFilterList(old => old.filter(f => f !== filter));
      } else {
        setFilterList(old => [...old, filter]);
      }
    },
    [filterList]
  );

  const filtredAuctions = useMemo(() => {
    // Enquanto o snapshot (staticAuctions) não estiver pronto, usa a lista
    // ao vivo para não zerar a listagem no render em que o painel abre.
    const baseList = showFilter && staticAuctions.length > 0 ? staticAuctions : auctions;

    if (auctions.length === 0) return { data: [], lengths: {} };

    // Listagem: aplica AND de todos os filtros ativos.
    const list = applyFilters(baseList, filterList, profile, launchWindowsDays);

    // Contadores dinâmicos (funil): cada chip reflete quantos veículos
    // atenderiam se fosse combinado (AND) com os filtros já selecionados.
    const lengths = getLengths(baseList, filterList, profile, launchWindowsDays);
    return { data: list, lengths };
    // Obs: showFilter/staticAuctions são lidos aqui, mas de propósito NÃO entram
    // nas dependências. Incluí-los faz o memo recalcular no render em que o painel
    // abre — antes do efeito que popula staticAuctions — zerando a listagem.
  }, [auctions, filterList, profile, launchWindowsDays]);

  const clearFilter = useCallback(() => {
    setFilterList([]);
    sessionStorage.removeItem('categories');
  }, [filtredAuctions]);

  useEffect(() => {
    if (showFilter) setStaticAuctions(auctions);
  }, [showFilter]);

  useEffect(() => {
    if (filterList.length > 0) {
      trackFilter('categories', filterList, filtredAuctions?.data?.length);
    }
  }, [filterList, filtredAuctions]);

  return (
    <FilterContext.Provider
      value={{
        filterList,
        filtredAuctions,
        toogleFilter,
        clearFilter,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilter() {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error('useFilter must be used within an FilterProvider!!');
  }

  return context;
}
